import {
  CheckCircleOutline,
  CloudUpload,
  InfoOutlined,
  PendingOutlined,
} from '@mui/icons-material';
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import React from 'react';
import {
  CustomContainer,
  customDataGridLocaleTextAll,
  customDataGridLocaleTextLoading,
  customDataGridLocaleTextNoData,
  StyledDataGrid,
} from '../../../styles/styles';
import UpdateButton from '../../atoms/Button/UpdateButton';
import { getJwtTokenFromIdToken } from '../auth/AuthFanc';
import { fetchErrorText } from '../../../common/const';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../../molecules/ConfirmDialog';

interface FirmwareBinaryFile {
  autoFirmwareBinaryFileId: number;
  fileName: string;
}

interface Sdc {
  autoSdcId: number;
  sdcUuid: string;
  sdcSerialNumber: string;
  doorPlace: string;
}

interface FirmwareUpdate {
  createdAt: Date;
  updatedAt: Date;
  autoFirmwareUpdateId: number;
  scheduledAt: Date;
  isConducted: boolean;
  firmwareBinaryFile: FirmwareBinaryFile;
  sdcs: Sdc[];
}

interface SdcListDialogProps {
  open: boolean;
  close: () => void;
  sdcs: Sdc[];
}

const SdcListDialog: React.FC<SdcListDialogProps> = (
  props: SdcListDialogProps
) => {
  return (
    <Dialog open={props.open} onClose={props.close} fullWidth maxWidth="sm">
      <DialogTitle>対象SDC一覧</DialogTitle>
      <DialogContent>
        {props.sdcs.map((sdc, i) => {
          return (
            <Box key={`sdcs-${i}`}>
              <Box sx={{ display: 'flex' }}>
                <Stack direction="column" textAlign="right" sx={{ mr: 1 }}>
                  <Typography variant="body2">シリアルナンバー：</Typography>
                  <Typography variant="body2">UUID：</Typography>
                  <Typography variant="body2">設置場所：</Typography>
                </Stack>
                <Stack direction="column" textAlign="left">
                  <Typography variant="body2">{sdc.sdcSerialNumber}</Typography>
                  <Typography variant="body2">{sdc.sdcUuid}</Typography>
                  <Typography variant="body2">{sdc.doorPlace}</Typography>
                </Stack>
              </Box>
              {i !== props.sdcs.length - 1 && <Divider sx={{ my: 1 }} />}
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.close} color="primary">
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const OtaListPage: React.FC = () => {
  const navigate = useNavigate();

  const [isSnackbarOpened, setIsSnackbarOpened] =
    React.useState<boolean>(false);
  const [snackbarText, setSnackbarText] = React.useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] =
    React.useState<AlertColor>('success');

  const [firmwareUpdates, setFirmwareUpdates] = React.useState<
    FirmwareUpdate[]
  >([]);
  const [filterIncomingFirmwareUpdates, setFilterIncomingFirmwareUpdates] =
    React.useState<boolean>(true);

  const [isFirmwareUpdateListLoading, setIsFirmwareUpdateListLoading] =
    React.useState<boolean>(false);

  const [
    sdcsDialogOpenedFirmwareUpdateId,
    setSdcsDialogOpenedFirmwareUpdateId,
  ] = React.useState<number | null>(null);
  const [sdcsToShowInDialog, setSdcsToShowInDialog] = React.useState<Sdc[]>([]);

  const [isDeletionInProgress, setIsDeletionInProgress] =
    React.useState<boolean>(false);
  const [deletionOngoingFirmwareUpdateId, setDeletionOngoingFirmwareUpdateId] =
    React.useState<number | null>(null);
  const [isDeleteConfirmDialogOpened, setIsDeleteConfirmDialogOpened] =
    React.useState<boolean>(false);

  const openSnackBarWithSuccess = (text: string) => {
    setIsSnackbarOpened(true);
    setSnackbarText(text);
    setSnackbarSeverity('success');
  };

  const openSnackBarWithError = (text: string) => {
    setIsSnackbarOpened(true);
    setSnackbarText(text);
    setSnackbarSeverity('error');
  };

  const handleCloseSnackbar = () => {
    setIsSnackbarOpened(false);
  };

  const fetchFirmwareUpdates = async () => {
    setIsFirmwareUpdateListLoading(true);
    await getJwtTokenFromIdToken()
      .then((token) => {
        const queryParams = filterIncomingFirmwareUpdates ? '?c=0' : '';
        // call listFirmwareUpdateEvents API
        return fetch(
          `${process.env.REACT_APP_API_ENDPOINT}/firmware-updates` +
            queryParams,
          {
            headers: {
              Authorization: token,
            },
          }
        );
      })
      .then((response) => {
        if (!response.ok) {
          console.error(`HTTP error! status: ${response.status}`);
          openSnackBarWithError(fetchErrorText);
          return null;
        }
        return response.json();
      })
      .then((body) => {
        if (!body) {
          return;
        }
        setFirmwareUpdates(
          body.firmwareUpdates.map((firmwareUpdate: any) => {
            return {
              ...firmwareUpdate,
              createdAt: new Date(firmwareUpdate.createdAt),
              updatedAt: new Date(firmwareUpdate.updatedAt),
              scheduledAt: new Date(firmwareUpdate.scheduledAt),
            };
          })
        );
      })
      .catch((e) => {
        openSnackBarWithError(fetchErrorText);
        console.error(e);
      })
      .finally(() => {
        setIsFirmwareUpdateListLoading(false);
      });
  };

  const fetchDeleteFirmwareUpdate = async () => {
    setIsDeletionInProgress(true);
    await getJwtTokenFromIdToken()
      .then((token) => {
        // call deleteFirmwareUpdateEvent API
        return fetch(
          `${process.env.REACT_APP_API_ENDPOINT}/firmware-updates/${deletionOngoingFirmwareUpdateId}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: token,
            },
          }
        );
      })
      .then((response) => {
        if (!response.ok) {
          console.error(`HTTP error! status: ${response.status}`);
          openSnackBarWithError(fetchErrorText);
          return null;
        }
        return response.json();
      })
      .then((body) => {
        if (!body) {
          return;
        }
        setFirmwareUpdates(
          firmwareUpdates.filter((firmwareUpdate) => {
            return (
              firmwareUpdate.autoFirmwareUpdateId !==
              deletionOngoingFirmwareUpdateId
            );
          })
        );
        openSnackBarWithSuccess('OTA実行予約を削除しました。');
      })
      .catch((e) => {
        openSnackBarWithError(fetchErrorText);
        console.error(e);
      })
      .finally(() => {
        setIsDeletionInProgress(false);
        setDeletionOngoingFirmwareUpdateId(null);
      });
  };

  const columns: GridColDef[] = [
    {
      field: 'isConducted',
      headerName: '状態',
      flex: 1,
      renderCell: (params: GridRenderCellParams<FirmwareUpdate>) => {
        const isConducted: boolean = params.value;
        return (
          <Tooltip title={isConducted ? '実行済み' : '未実行'}>
            {isConducted ? (
              <CheckCircleOutline color="success" />
            ) : (
              <PendingOutlined color="warning" />
            )}
          </Tooltip>
        );
      },
    },
    {
      field: 'scheduledAt',
      headerName: '実行予定日時',
      flex: 2,
      renderCell: (params: GridRenderCellParams<FirmwareUpdate>) => {
        const scheduledAt: Date = params.value;
        const datetimeStr = scheduledAt.toLocaleString('ja-JP', {
          timeZone: 'Asia/Tokyo',
        });
        return (
          <Tooltip title={datetimeStr}>
            <span>{datetimeStr}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'sdcs',
      headerName: '対象SDC',
      flex: 2,
      renderCell: (params: GridRenderCellParams<FirmwareUpdate>) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ mr: 1 }}>{`${params.row.sdcs.length} 台`}</Box>
            <Tooltip title="SDC情報の一覧を表示">
              <IconButton
                size="small"
                onClick={() => {
                  setSdcsToShowInDialog(params.row.sdcs);
                  setSdcsDialogOpenedFirmwareUpdateId(
                    params.row.autoFirmwareUpdateId
                  );
                }}
              >
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'firmware',
      headerName: 'ファームウェアファイル名',
      flex: 3,
      renderCell: (params: GridRenderCellParams<FirmwareUpdate>) => (
        <Tooltip title={params.row.firmwareBinaryFile.fileName}>
          <Typography variant="body2" fontFamily="monospace">
            {params.row.firmwareBinaryFile.fileName}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'operations',
      headerName: '操作',
      flex: 1,
      renderCell: (params: GridRenderCellParams<FirmwareUpdate>) => {
        return (
          <>
            {!params.row.isConducted && (
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => {
                  setDeletionOngoingFirmwareUpdateId(
                    params.row.autoFirmwareUpdateId
                  );
                  setIsDeleteConfirmDialogOpened(true);
                }}
                disabled={isDeletionInProgress}
              >
                {deletionOngoingFirmwareUpdateId ===
                params.row.autoFirmwareUpdateId ? (
                  <CircularProgress size={15} sx={{ color: 'white', m: 0.5 }} />
                ) : (
                  '削除'
                )}
              </Button>
            )}
          </>
        );
      },
    },
  ];

  React.useEffect(() => {
    // call GET API
    fetchFirmwareUpdates();
  }, [filterIncomingFirmwareUpdates]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <CustomContainer maxWidth={false}>
      <Box display="flex" flexDirection="column" marginBottom={2}>
        <Typography variant="h5" component="h2" gutterBottom>
          OTA実行予約一覧
        </Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                checked={filterIncomingFirmwareUpdates}
                onChange={(event) =>
                  setFilterIncomingFirmwareUpdates(event.target.checked)
                }
              />
            }
            label="未実行のみ表示"
          />
          <Box sx={{ display: 'flex' }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                navigate('/ota-schedules/new');
              }}
              endIcon={<CloudUpload />}
              sx={{ mr: 1 }}
            >
              新規OTA実行予約
            </Button>
            <UpdateButton onClick={fetchFirmwareUpdates} />
          </Box>
        </Box>
      </Box>
      <div style={{ height: 700, width: '100%' }}>
        <StyledDataGrid
          rows={firmwareUpdates}
          columns={columns}
          getRowId={(row) => row.autoFirmwareUpdateId}
          disableRowSelectionOnClick
          localeText={
            isFirmwareUpdateListLoading
              ? customDataGridLocaleTextLoading
              : firmwareUpdates.length === 0
              ? customDataGridLocaleTextNoData
              : customDataGridLocaleTextAll
          }
        />
      </div>

      <SdcListDialog
        open={sdcsDialogOpenedFirmwareUpdateId !== null}
        close={() => {
          setSdcsDialogOpenedFirmwareUpdateId(null);
        }}
        sdcs={sdcsToShowInDialog}
      />

      <ConfirmDialog
        open={isDeleteConfirmDialogOpened}
        onClose={() => {
          setIsDeleteConfirmDialogOpened(false);
          setDeletionOngoingFirmwareUpdateId(null);
        }}
        onConfirm={fetchDeleteFirmwareUpdate}
        title="確認"
        description="本当に削除しますか？"
      />

      <Snackbar
        open={isSnackbarOpened}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarText}
        </Alert>
      </Snackbar>
    </CustomContainer>
  );
};
